import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { TimedPoint } from "@/lib/replay-core";
import { interpolatePoint } from "@/lib/replay-core";

export interface ReplayPlayerState {
  elapsed: number;
  playing: boolean;
  total: number;
  speed: number;
}

export function useReplayPlayer(points: TimedPoint[]) {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const frameRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);

  const total = points.length
    ? points[points.length - 1].elapsed_seconds
    : 0;

  useEffect(() => {
    if (!playing) {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      lastTsRef.current = null;
      return;
    }
    const tick = (ts: number) => {
      if (lastTsRef.current === null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;
      setElapsed((prev) => {
        const next = prev + dt * speed;
        if (next >= total) {
          setPlaying(false);
          return total;
        }
        return next;
      });
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      lastTsRef.current = null;
    };
  }, [playing, speed, total]);

  const currentPoint = useMemo(
    () => (points.length ? interpolatePoint(points, elapsed) : null),
    [points, elapsed],
  );

  const play = useCallback(() => setPlaying(true), []);
  const pause = useCallback(() => setPlaying(false), []);
  const seek = useCallback(
    (value: number) => setElapsed(Math.max(0, Math.min(total, value))),
    [total],
  );
  const reset = useCallback(() => {
    setElapsed(0);
    setPlaying(false);
  }, []);

  return {
    elapsed,
    playing,
    total,
    speed,
    currentPoint,
    setSpeed,
    play,
    pause,
    seek,
    reset,
  };
}
